import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ShoppingBag, ArrowLeft, TrendingUp, Clock, Users } from 'lucide-react';

const RetailCaseStudy = () => {
  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <article className="pt-40 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Back Link */}
          <Link to="/" className="inline-flex items-center gap-2 text-[#2563EB] hover:text-[#3B82F6] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center">
                <ShoppingBag className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="text-sm text-[#2563EB] font-semibold uppercase tracking-wide">
                  Retail & E commerce
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#F8FAFC]">
                  GreenLeaf Home & Garden
                </h1>
              </div>
            </div>
            <p className="text-xl text-[#94A3B8]">
              How a multi-channel home goods retailer increased profit margins by 32% in 90 days
            </p>
          </div>

          {/* Hero Image */}
          <div className="relative rounded-2xl overflow-hidden mb-12 border border-[#2563EB]/20">
            <img
              src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200"
              alt="GreenLeaf Home & Garden Store"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C]/60 to-transparent" />
          </div>

          {/* Company Overview */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Users className="w-8 h-8 text-[#2563EB] mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">2 Locations + Shopify</div>
              <div className="text-sm text-[#94A3B8]">Multi-channel retail</div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <TrendingUp className="w-8 h-8 text-[#2563EB] mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">340 SKUs</div>
              <div className="text-sm text-[#94A3B8]">Home & garden products</div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Clock className="w-8 h-8 text-[#2563EB] mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">9 Months</div>
              <div className="text-sm text-[#94A3B8]">Time to full ROI</div>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-invert prose-lg max-w-none">
            <div className="text-[#94A3B8] leading-relaxed space-y-8">
              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-red-500/30 rounded-xl p-8">
                <h2 className="text-3xl font-bold text-[#F8FAFC] mb-4">The Challenge</h2>
                <p className="text-lg">
                  HomeStyle Furnishings was managing pricing for over 8,000 SKUs across 12 locations using a complex web of Excel spreadsheets. Their pricing manager, Rebecca Martinez, spent 20+ hours each week manually updating prices based on competitor research, seasonal trends, and gut feeling.
                </p>
                <ul className="list-disc pl-6 space-y-2 text-[#94A3B8] mt-4">
                  <li>Competitive pricing data was always 2 to 3 weeks out of date</li>
                  <li>No systematic way to test price elasticity across product categories</li>
                  <li>Regional pricing differences were being ignored due to complexity</li>
                  <li>Markdown decisions were reactive rather than strategic</li>
                  <li>No visibility into which products were margin positive vs margin negative</li>
                </ul>
                <p className="mt-4">
                  Despite solid foot traffic and customer satisfaction scores, margins kept shrinking. The business was growing revenue but not profitability. Margins had declined for three consecutive quarters.
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-8">
                <h2 className="text-3xl font-bold text-[#F8FAFC] mb-4">The Solution</h2>
                <p className="text-lg mb-4">
                  In March 2024, HomeStyle implemented PriceStrike across all locations. The transformation happened in three phases:
                </p>

                <h3 className="text-2xl font-bold text-[#F8FAFC] mt-6 mb-3">Phase 1: Data Foundation (Months 1 to 2)</h3>
                <p>
                  Consolidated all pricing data into a single intelligent platform. PriceStrike integrated with their existing POS system and e commerce platform, automatically pulling in sales data, cost information, and inventory levels.
                </p>
                <p>
                  Simultaneously, the platform began monitoring competitor pricing across 15 major competitors, tracking over 3,000 comparable products daily. For the first time, HomeStyle had real time visibility into their competitive position.
                </p>

                <h3 className="text-2xl font-bold text-[#F8FAFC] mt-6 mb-3">Phase 2: Strategic Repricing (Months 3 to 5)</h3>
                <p>
                  Armed with elasticity models and competitive intelligence, HomeStyle began strategic repricing. Key moves included:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-[#94A3B8]">
                  <li>Raising prices 8 to 15% on premium furniture collections where they had clear differentiation</li>
                  <li>Implementing location based pricing that reflected local market conditions</li>
                  <li>Creating dynamic promotional strategies that protected margin</li>
                  <li>Optimizing markdown timing based on inventory velocity and seasonal patterns</li>
                </ul>

                <h3 className="text-2xl font-bold text-[#F8FAFC] mt-6 mb-3">Phase 3: Optimization (Months 6 to 9)</h3>
                <p>
                  PriceStrike began automatically adjusting prices within approved guardrails based on real time market conditions. The team also implemented A/B testing for the first time, systematically learning which price points maximized profitability.
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-green-500/30 rounded-xl p-8">
                <h2 className="text-3xl font-bold text-[#F8FAFC] mb-6">The Results</h2>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="text-center p-6 bg-[#0A0F1C]/50 rounded-xl border border-[#2563EB]/20">
                    <div className="text-5xl font-bold text-[#2563EB] mb-2">28%</div>
                    <div className="text-[#F8FAFC] font-semibold mb-1">Margin Increase</div>
                    <div className="text-sm text-[#94A3B8]">Overall gross margin improvement across all categories</div>
                  </div>
                  <div className="text-center p-6 bg-[#0A0F1C]/50 rounded-xl border border-[#2563EB]/20">
                    <div className="text-5xl font-bold text-[#2563EB] mb-2">12%</div>
                    <div className="text-[#F8FAFC] font-semibold mb-1">Revenue Growth</div>
                    <div className="text-sm text-[#94A3B8]">Same store sales increase year over year</div>
                  </div>
                  <div className="text-center p-6 bg-[#0A0F1C]/50 rounded-xl border border-[#2563EB]/20">
                    <div className="text-5xl font-bold text-[#2563EB] mb-2">90%</div>
                    <div className="text-[#F8FAFC] font-semibold mb-1">Time Savings</div>
                    <div className="text-sm text-[#94A3B8]">Reduction in manual pricing work</div>
                  </div>
                  <div className="text-center p-6 bg-[#0A0F1C]/50 rounded-xl border border-[#2563EB]/20">
                    <div className="text-5xl font-bold text-[#2563EB] mb-2">4.8x</div>
                    <div className="text-[#F8FAFC] font-semibold mb-1">ROI</div>
                    <div className="text-sm text-[#94A3B8]">Return on investment in year one</div>
                  </div>
                </div>
                <p className="text-lg">
                  Perhaps more importantly, the quality of pricing decisions improved dramatically. Rebecca and her team shifted from spending time in spreadsheets to doing strategic analysis and collaborating with merchandising on assortment planning.
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#2563EB]/10 to-transparent border border-[#2563EB]/30 rounded-xl p-8">
                <div className="text-4xl text-[#2563EB] mb-4">"</div>
                <p className="text-[#F8FAFC] text-2xl leading-relaxed mb-6 italic">
                  We went from guessing at prices to knowing our optimal price points. The margin improvement paid for the technology ten times over, but the real value is the confidence we now have in our pricing strategy.
                </p>
                <div className="border-t border-[#2563EB]/20 pt-4">
                  <div className="font-semibold text-[#F8FAFC] text-lg">Rebecca Martinez</div>
                  <div className="text-[#94A3B8]">Pricing Manager</div>
                  <div className="text-sm text-[#2563EB] mt-1">HomeStyle Furnishings</div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 p-8 bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-2xl text-center">
            <h3 className="text-3xl font-bold text-[#F8FAFC] mb-4">
              Ready to Transform Your Pricing?
            </h3>
            <p className="text-[#94A3B8] mb-6 text-lg">
              See how PriceStrike can deliver similar results for your retail business.
            </p>
            <Link to="/pricing" className="inline-block bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-4 rounded-lg font-semibold transition-colors text-lg">
              Explore Pricing Plans
            </Link>
          </div>
        </div>
      </article>
      
      <Footer />
    </div>
  );
};

export default RetailCaseStudy;
